import type { ReactNode } from 'react'

import type { ControlledColumn } from '@/design/DataTable'

/*
 * A server-sorted list's columns, and when a null is missing data rather than undefined:
 * the column-wide state the headers, notes and column chooser read from a list's own
 * per-row rule.
 */

export interface ListColumn<Row, Context> {
  /** The API's sort key for a sortable column, else the column's own name. */
  key: string
  label: string
  /** The column chooser's group. */
  group: string
  /** The row field that may be null for a reason the API names. */
  field?: keyof Row
  /** Sortable on the server by this key. */
  sort?: string
  /** Shown until the viewer chooses otherwise. */
  byDefault: boolean
  align?: 'start' | 'end'
  cell: (row: Row, context: Context) => ReactNode
}

/** Each row field the API names as null for a reason, and the reason. */
export type NotAvailable<Row, Reason extends string> = Partial<Record<keyof Row, Reason>>

/**
 * A list's one rule for a null: why a row's null in `field` is missing data rather than
 * undefined, or none. Cells read it per row; `columnState` reads it across rows.
 */
export type ReasonRule<Row, Reason extends string> = (
  row: Row,
  field: keyof Row,
  notAvailable: NotAvailable<Row, Reason>,
) => Reason | null

/**
 * How a list words a reason: `label` for a missing figure ("Not live yet", "Not synced
 * yet"), on its cells and on a column missing it on every row, and `why` for the tooltip
 * and the note.
 */
export type DescribeReason<Reason extends string> = (reason: Reason) => { label: string; why: string }

/** Why a column's figures are missing, and whether for every row or only some. */
export interface ColumnState<Reason extends string> {
  reason: Reason
  /** Every loaded row lacks it for `reason`. Otherwise only some rows do, and their cells say why. */
  whole: boolean
}

/**
 * A column's missing figures, from `rule` across the loaded rows; null when the API names
 * no reason for its field. The header, the notes and the column chooser all read this,
 * so they always agree with the cells.
 */
export function columnState<Row, Reason extends string>(
  column: { field?: keyof Row },
  rows: readonly Row[],
  notAvailable: NotAvailable<Row, Reason>,
  rule: ReasonRule<Row, Reason>,
): ColumnState<Reason> | null {
  const { field } = column
  const reason = field ? notAvailable[field] : undefined
  if (!field || !reason) return null
  const whole = rows.length > 0 && rows.every((row) => row[field] === null && rule(row, field, notAvailable) === reason)
  return { reason, whole }
}

/** The table's columns: the chosen ones, a header whose whole column is empty for a reason saying so in the list's words ("· Not live yet"). */
export function tableColumns<Row, Context extends { notAvailable: NotAvailable<Row, Reason> }, Reason extends string>(
  chosen: readonly ListColumn<Row, Context>[],
  rows: readonly Row[],
  context: Context,
  rule: ReasonRule<Row, Reason>,
  describe: DescribeReason<Reason>,
): ControlledColumn<Row>[] {
  return chosen.map((column) => {
    const state = columnState(column, rows, context.notAvailable, rule)
    return {
      key: column.key,
      header: state?.whole ? `${column.label} · ${describe(state.reason).label}` : column.label,
      cell: (row) => column.cell(row, context),
      sortable: column.sort !== undefined,
      align: column.align,
    }
  })
}
